import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  compactPartsAuthenticity,
  type PartsAuthenticityMap,
} from '@redevicer/shared'
import BatteryCyclesWheelPicker, { BatteryCyclesValueChip } from './BatteryCyclesWheelPicker'
import BatteryWheelPicker from './BatteryWheelPicker'
import BuilderSegmentedControl from './BuilderSegmentedControl'
import BuilderBatteryTestedField from './BuilderBatteryTestedField'
import ListingSpecCell from './ListingSpecCell'
import BatteryHealthValueChip from './BatteryHealthValueChip'

function levelFromBatteryHealth(value: string): number {
  const trimmed = value.trim()
  const parsed = parseInt(trimmed, 10)
  if (trimmed === '' || !Number.isFinite(parsed)) return 100
  const n = Math.min(100, Math.max(0, parsed))
  return n
}

type BatteryOrigin = 'original' | 'replaced' | 'unknown'
type ReplacementKind = 'oem' | 'aftermarket' | 'unknown'

function readBatteryOrigin(parts: PartsAuthenticityMap, batteryOriginal: boolean): BatteryOrigin {
  const st = parts.battery?.status
  if (st === 'original') return 'original'
  if (st === 'replaced_oem' || st === 'replaced_aftermarket' || st === 'replaced') return 'replaced'
  if (st === 'unknown') return 'unknown'
  return batteryOriginal ? 'original' : 'replaced'
}

function readReplacementKind(parts: PartsAuthenticityMap): ReplacementKind {
  const st = parts.battery?.status
  if (st === 'replaced_oem') return 'oem'
  if (st === 'replaced_aftermarket') return 'aftermarket'
  return 'unknown'
}

type Props = {
  batteryHealth: string
  onBatteryHealthChange: (value: string) => void
  batteryLevel: number
  batteryTone: string
  batteryCycles: string
  onBatteryCyclesChange: (value: string) => void
  batteryTestedAt: string
  onBatteryTestedAtChange: (value: string) => void
  partsAuth: PartsAuthenticityMap
  onPartsAuthChange: (next: PartsAuthenticityMap) => void
  batteryOriginal: boolean
  onBatteryOriginalChange: (value: boolean) => void
}

export default function BatteryTransparencyBuilderPanel({
  batteryHealth,
  onBatteryHealthChange,
  batteryLevel: _batteryLevel,
  batteryTone: _batteryTone,
  batteryCycles,
  onBatteryCyclesChange,
  batteryTestedAt,
  onBatteryTestedAtChange,
  partsAuth,
  onPartsAuthChange,
  batteryOriginal,
  onBatteryOriginalChange,
}: Props) {
  const { t } = useTranslation()
  const origin = useMemo(
    () => readBatteryOrigin(partsAuth, batteryOriginal),
    [partsAuth, batteryOriginal],
  )
  const replacementKind = useMemo(() => readReplacementKind(partsAuth), [partsAuth])

  const originOptions = useMemo(
    () =>
      (
        [
          ['original', t('batteryTransparency.form.originOriginal')],
          ['replaced', t('batteryTransparency.form.originReplaced')],
          ['unknown', t('batteryTransparency.form.originUnknown')],
        ] as const
      ).map(([value, label]) => ({ value, label })),
    [t],
  )

  const replacementOptions = useMemo(
    () =>
      (
        [
          ['oem', t('batteryTransparency.form.replacementOem')],
          ['aftermarket', t('batteryTransparency.form.replacementThirdParty')],
          ['unknown', t('batteryTransparency.form.replacementUnknown')],
        ] as const
      ).map(([value, label]) => ({ value, label })),
    [t],
  )

  function applyOrigin(next: BatteryOrigin) {
    if (next === 'original') {
      onBatteryOriginalChange(true)
      onPartsAuthChange(
        compactPartsAuthenticity({ ...partsAuth, battery: { status: 'original' } }),
      )
      return
    }
    if (next === 'unknown') {
      onBatteryOriginalChange(true)
      onPartsAuthChange(
        compactPartsAuthenticity({ ...partsAuth, battery: { status: 'unknown' } }),
      )
      return
    }
    onBatteryOriginalChange(false)
    const kind = readReplacementKind(partsAuth)
    const status =
      kind === 'oem' ? 'replaced_oem' : kind === 'aftermarket' ? 'replaced_aftermarket' : 'replaced'
    onPartsAuthChange(compactPartsAuthenticity({ ...partsAuth, battery: { status } }))
  }

  function applyReplacement(kind: ReplacementKind) {
    onBatteryOriginalChange(false)
    const status =
      kind === 'oem' ? 'replaced_oem' : kind === 'aftermarket' ? 'replaced_aftermarket' : 'replaced'
    onPartsAuthChange(compactPartsAuthenticity({ ...partsAuth, battery: { status } }))
  }

  return (
    <section className="builder-battery-transparency" aria-labelledby="builder-battery-transparency-title">
      <h4 id="builder-battery-transparency-title" className="builder-battery-transparency__title">
        {t('batteryTransparency.title')}
      </h4>
      <div className="builder-battery-transparency__pickers-row">
        <ListingSpecCell
          tone="battery"
          label={t('listing.form.batteryLabel')}
          valueChip={
            <BatteryHealthValueChip
              value={batteryHealth}
              onChange={onBatteryHealthChange}
              ariaLabel={t('listing.form.batteryLabel')}
            />
          }
        >
          <BatteryWheelPicker
            compact
            value={levelFromBatteryHealth(batteryHealth)}
            onChange={(n) => onBatteryHealthChange(String(n))}
          />
        </ListingSpecCell>

        <ListingSpecCell
          tone="storage"
          label={t('batteryTransparency.form.cycles')}
          valueChip={
            <BatteryCyclesValueChip
              value={batteryCycles}
              onChange={onBatteryCyclesChange}
              ariaLabel={t('batteryTransparency.form.cycles')}
            />
          }
        >
          <BatteryCyclesWheelPicker
            compact
            showValueChip={false}
            value={batteryCycles}
            onChange={onBatteryCyclesChange}
            ariaLabel={t('batteryTransparency.form.cycles')}
          />
        </ListingSpecCell>
      </div>

      <div className="builder-battery-transparency__details-row">
        <div className="builder-history-field builder-history-field--segmented">
          <span className="builder-history-field__label">{t('batteryTransparency.form.originLabel')}</span>
          <div className="parts-auth-form--builder-detail builder-history-segments">
            <BuilderSegmentedControl
              ariaLabel={t('batteryTransparency.form.originLabel')}
              value={origin}
              options={originOptions}
              onChange={(v) => applyOrigin(v as BatteryOrigin)}
            />
          </div>
          <div
            className={`builder-history-field__sub-slot${
              origin === 'replaced' ? ' builder-history-field__sub-slot--filled' : ''
            }`}
          >
            {origin === 'replaced' ? (
              <div className="builder-history-field__sub parts-auth-form--builder-detail">
                <span className="builder-history-field__sublabel">
                  {t('batteryTransparency.form.replacementTypeLabel')}
                </span>
                <div className="builder-history-segments">
                  <BuilderSegmentedControl
                    ariaLabel={t('batteryTransparency.form.replacementTypeLabel')}
                    value={replacementKind}
                    options={replacementOptions}
                    onChange={(v) => applyReplacement(v as ReplacementKind)}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="builder-history-field builder-history-field--date">
          <span className="builder-history-field__label">{t('batteryTransparency.form.tested')}</span>
          <BuilderBatteryTestedField
            value={batteryTestedAt}
            max={new Date().toISOString().slice(0, 10)}
            onChange={onBatteryTestedAtChange}
            ariaLabel={t('batteryTransparency.form.tested')}
            preferAbove
          />
        </div>
      </div>
    </section>
  )
}
